import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { CircleCheck, Info, KeyRound } from 'lucide-react';
import BlueprintWithMarkers from '../../Components/BlueprintWithMarkers';
import { useOpenPath } from '../../hooks/useOpenPath';
import { Unit } from '../../types/units';
import { UnitKind, UnitTenure } from '../../types/Enums';
import { translate } from '../../types/EnumTranslations';
import { Building, LevelModel, RoomModel } from '../../types/protocols';
import { RoomImpl } from '../../types/implementations';
import { isUnitLeasable, isUnitRented, unitDisplayName, unitTypeIcon } from '../../Helpers/unitUtils';
import { levelDisplayName } from '../../Helpers/levelDisplayName';
import { unitLevelPanels } from './unitsView';

interface UnitDrawingsPaneProps {
    building: Building;
    unit: Unit;
    levels: { [levelKey: string]: LevelModel };
    rooms: { [roomKey: string]: RoomModel };
    stairwellName?: string;
    /** The current tenant, admins only. */
    tenantName?: string;
    /** Detaljer (the detail modal); left out when the detail pane already shows the unit. */
    onShowDetails?: () => void;
    /** Back to the level's drawing with every unit on it. */
    onShowLevel: (levelKey: string) => void;
}

const formatArea = (area: number): string => `${area.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} m²`;

/**
 * The right pane for the selected unit: its facts, then one block per
 * level its rooms are on, highest first, each with the rooms as links and the
 * level's drawing with those rooms marked. A room hovered in the list is
 * enlarged on the drawing.
 */
const UnitDrawingsPane: React.FC<UnitDrawingsPaneProps> = ({ building, unit, levels, rooms, stairwellName, tenantName, onShowDetails, onShowLevel }) => {
    const openPath = useOpenPath();
    const [hoveredRoomKey, setHoveredRoomKey] = useState<string | null>(null);
    const panels = useMemo(() => unitLevelPanels(unit, levels, rooms), [unit, levels, rooms]);

    const isApartment = unit.kind === UnitKind.Apartment;
    const facts: string[] = [];
    if (stairwellName) facts.push(`Trappuppgång ${stairwellName}`);
    facts.push(translate(unit.tenure));
    if (isApartment && unit.roomCount) facts.push(`${unit.roomCount} rok`);
    if (!isApartment && unit.commercialType) facts.push(unit.commercialType);
    if (unit.area) facts.push(`${formatArea(unit.area)} ${isApartment ? 'BOA' : 'LOA'}`);

    return (
        <div className="units-drawings-pane">
            <div className="units-drawings-header">
                <img className="units-drawings-icon" src={unitTypeIcon(unit.kind)} alt="" aria-hidden="true" />
                <div className="units-drawings-title">
                    <h4>{unitDisplayName(unit)}</h4>
                    <div className="units-drawings-facts">
                        <span>{facts.join(' · ')}</span>
                        {isUnitLeasable(unit) && (isUnitRented(unit)
                            ? <span className="units-badge units-badge--muted"><KeyRound size={12} aria-hidden="true" />Uthyrd{tenantName ? ` · ${tenantName}` : ''}</span>
                            : <span className="units-badge units-badge--success"><CircleCheck size={12} aria-hidden="true" />Ledig</span>)}
                        {unit.tenure === UnitTenure.Own && <span className="units-badge">Egen</span>}
                    </div>
                </div>
                {onShowDetails && (
                    <Button variant="outline-primary" size="sm" onClick={onShowDetails}>
                        <Info size={14} className="me-1" />Detaljer
                    </Button>
                )}
            </div>

            {panels.length === 0 && (
                <div className="units-blueprint-placeholder">
                    <span>Inga rum kopplade: enheten visas inte på någon ritning.</span>
                    <small>Koppla rum under Detaljer › Redigera.</small>
                </div>
            )}

            {panels.map(({ level, blueprintKey, rooms: levelRooms }) => {
                const blueprint = blueprintKey ? level.blueprints?.[blueprintKey] : undefined;
                return (
                    <section key={level.key} className="units-level-block">
                        <div className="units-level-block-header">
                            <h5>{levelDisplayName(level)}</h5>
                            <Button variant="link" size="sm" onClick={() => onShowLevel(level.key)}>
                                Alla enheter på våningen
                            </Button>
                        </div>
                        <div className="units-level-rooms">
                            {levelRooms.map(room => {
                                const placed = !!blueprintKey && !!room.blueprintLocations?.[blueprintKey];
                                return (
                                    <Link
                                        key={room.key}
                                        to={`/building/${building.key}/room/${room.key}`}
                                        className={`units-component-chip ${placed ? '' : 'units-component-chip--unplaced'}`}
                                        title={placed ? undefined : 'Inte placerat på ritningen'}
                                        onMouseEnter={() => setHoveredRoomKey(room.key)}
                                        onMouseLeave={() => setHoveredRoomKey(null)}
                                    >
                                        {new RoomImpl(room).displayName()}
                                    </Link>
                                );
                            })}
                        </div>
                        {blueprint && blueprintKey ? (
                            <BlueprintWithMarkers
                                blueprint={blueprint}
                                currentBlueprintKey={blueprintKey}
                                building={building}
                                rooms={levelRooms.map(room => new RoomImpl(room))}
                                onRoomClick={(roomKey, event) => openPath(`/building/${building.key}/room/${roomKey}`, event)}
                                highlight={hoveredRoomKey && levelRooms.some(room => room.key === hoveredRoomKey) ? { kind: 'room', key: hoveredRoomKey } : undefined}
                                hideTitle
                            />
                        ) : (
                            <div className="units-blueprint-placeholder units-blueprint-placeholder--small">
                                <span>Ingen ritning uppladdad för denna våning.</span>
                            </div>
                        )}
                    </section>
                );
            })}
        </div>
    );
};

export default UnitDrawingsPane;
